import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import useCurrentUser from '~/hooks/useCurrentUser';
import useLocalStorage from '~/hooks/useLocalStorage';
import { useUserPromoRoles } from '~/hooks/useUserPromoRoles';

import { promoRoles } from '~/services/PromoService';
import { formatDateWithClock } from '~/services/TimeService';

export type PromoRecentVisit = { code: string; type: 'promo' | 'voucher'; href: string; visitedAt: string };
export const PROMO_RECENT_VISITS_KEY = 'promo:recent-visits:v1';
/** Same-window localStorage writes do not emit the browser `storage` event. */
const PROMO_RECENT_VISITS_UPDATED_EVENT = 'promo-recent-visits-updated';
let recentVisitsEnabled = false;

export function recordPromoVisit(visit: Omit<PromoRecentVisit, 'visitedAt'>): Array<PromoRecentVisit> | undefined {
  if (!recentVisitsEnabled) return;
  try {
    const existing = JSON.parse(window.localStorage.getItem(PROMO_RECENT_VISITS_KEY) || '[]') as PromoRecentVisit[];
    const next = [
      { ...visit, visitedAt: new Date().toISOString() },
      ...existing.filter((item) => item.code !== visit.code || item.type !== visit.type),
    ].slice(0, 50);
    window.localStorage.setItem(PROMO_RECENT_VISITS_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(PROMO_RECENT_VISITS_UPDATED_EVENT, { detail: next }));
    return next;
  } catch {
    // Storage is optional; navigation should never fail because it is unavailable.
  }
}

export default function PromoRecentVisits() {
  const { user } = useCurrentUser();
  const { hasRole } = useUserPromoRoles({ userId: user?.id_member ?? '', enabled: Boolean(user?.id_member) });
  const [storedVisits, setVisits] = useLocalStorage(PROMO_RECENT_VISITS_KEY, []);
  const visits = storedVisits as PromoRecentVisit[];
  const [showAll, setShowAll] = useState(false);
  const isBetaUser = hasRole(promoRoles.PROMO_BETA_TESTER_ROLE);
  useEffect(() => {
    recentVisitsEnabled = isBetaUser;
    return () => {
      recentVisitsEnabled = false;
    };
  }, [isBetaUser]);
  useEffect(() => {
    const syncVisits = (event: Event) => {
      const visitsUpdate = (event as CustomEvent<Array<PromoRecentVisit>>).detail;
      if (Array.isArray(visitsUpdate)) {
        setVisits(visitsUpdate);
      }
    };

    window.addEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncVisits);
    return () => window.removeEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncVisits);
  }, [setVisits]);
  const recent = useMemo(() => visits.slice(0, 10), [visits]);
  if (!isBetaUser || visits.length === 0) return null;
  const remove = (visit: PromoRecentVisit) => setVisits(visits.filter((item) => item !== visit));
  const renderVisit = (visit: PromoRecentVisit) => (
    <Button key={`${visit.type}-${visit.code}`} variant="text" size="small" href={visit.href}>
      {visit.code}
    </Button>
  );
  const columns: GridColDef<PromoRecentVisit>[] = [
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'type', headerName: 'Type', width: 120 },
    {
      field: 'visitedAt',
      headerName: 'Visited',
      width: 190,
      valueFormatter: (value) => formatDateWithClock(value as string),
    },
    {
      field: 'remove',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton aria-label={`Remove ${params.row.code}`} size="small" onClick={() => remove(params.row)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minHeight: 32, overflow: 'hidden' }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Recently visited:
        </Typography>
        {recent.map(renderVisit)}
        {visits.length > 10 && (
          <Button variant="text" size="small" onClick={() => setShowAll(true)}>
            Show all ({visits.length})
          </Button>
        )}
      </Stack>
      <Dialog open={showAll} onClose={() => setShowAll(false)} fullWidth maxWidth="md">
        <DialogTitle>Recently visited promo and voucher codes</DialogTitle>
        <DialogContent>
          <DataGrid
            autoHeight
            rows={visits}
            columns={columns}
            getRowId={(row) => `${row.type}-${row.code}`}
            hideFooter
            pageSizeOptions={[50]}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowAll(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
